"use client";

import { useNextRouterContext } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { LazyHeaderCategoriesDropdown } from "@faire/deprecated-retailer-visitor-shared/components/Header/desktop/HeaderCategoriesDropdown/LazyCategoriesDropdown";
import { HeaderDropdown } from "@faire/deprecated-retailer-visitor-shared/components/Header/styled";
import { useIsHeaderDropdownVisible } from "@faire/deprecated-retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { Chevron } from "@faire/deprecated-retailer-visitor-shared/icons/Chevron/Chevron";
import { useChatBubbleStore } from "@faire/deprecated-retailer-visitor-shared/packages/messenger/views/useChatBubbleStore";
import { useSettingNextJSRscCategoryPage } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { Core } from "@faire/slate/foundation/color";
import { trackNavigationTreeUxCategoriesMenuButtonClick } from "@faire/web-api--source/events/navigationTreeUx/click/categoriesMenuButton";
import { trackNavigationTreeUxCategoriesMenuButtonView } from "@faire/web-api--source/events/navigationTreeUx/view/categoriesMenuButton";
import { route as categoryC1Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Close } from "@faire/web/slate/icons/Close";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";

export const HeaderCategoriesButton: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();
  const [, setChatBubbleStore] = useChatBubbleStore([]);
  const isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();

  const toggleHandler = useCallback(
    (shouldShowDropdown: boolean, isUserClick: boolean) => {
      setShouldShowDropdown(shouldShowDropdown);
      setHeaderDropdownVisibility("CATEGORIES", shouldShowDropdown);

      if (shouldShowDropdown) {
        setChatBubbleStore({ isShowingChat: false });
      }

      if (isUserClick) {
        trackNavigationTreeUxCategoriesMenuButtonClick(
          shouldShowDropdown ? "OPEN" : "CLOSE"
        );
        if (shouldShowDropdown && !isRSCCategoryPageEnabled) {
          nextPrefetch(categoryC1Route);
        }
      }
    },
    [
      setHeaderDropdownVisibility,
      setChatBubbleStore,
      isRSCCategoryPageEnabled,
      nextPrefetch,
    ]
  );

  useEffect(() => {
    trackNavigationTreeUxCategoriesMenuButtonView();
  }, []);

  const closeDropdown = useCallback(() => {
    toggleHandler(false, false);
    buttonRef.current?.blur();
  }, [toggleHandler]);

  return (
    <ClickOutside onClickOutside={closeDropdown}>
      <DropdownButton
        buttonRef={buttonRef}
        onClick={() => toggleHandler(!shouldShowDropdown, true)}
        aria-label={strictLocalize({
          id: "P9kdue",
          defaultMessage: "All categories",
          description: { text: "A button that opens a menu of all categories" },
        })}
        aria-haspopup="menu"
        variant="tertiary"
        $isSelected={shouldShowDropdown}
      >
        <StrictLocalMsg
          id="P9kdue"
          defaultMessage="All categories"
          description={{
            text: "A button that opens a menu of all categories",
          }}
        />
        <Spacer size="1x" />
        <IconWrapper>
          {shouldShowDropdown ? <Close /> : <Chevron direction="down" />}
        </IconWrapper>
      </DropdownButton>
      {shouldShowDropdown ? (
        <HeaderDropdown $right="0px" $width="100vw" $noBoxShadow>
          <Suspense fallback={null}>
            <LazyHeaderCategoriesDropdown handleCloseMenu={closeDropdown} />
          </Suspense>
        </HeaderDropdown>
      ) : null}
    </ClickOutside>
  );
};

const DropdownButton = styled(Button)<{ $isSelected?: boolean }>`
  padding: ${getSpacing(10, "2x")};
  text-decoration: none;
  border-radius: ${getSpacing("4x")};

  ${(props) =>
    props.$isSelected
      ? css`
          background-color: ${Core.surface.tertiary};
          text-decoration: none;
        `
      : ""}

  &:hover,
  &:focus {
    background-color: ${Core.surface.tertiary};
    text-decoration: none;
  }

  &:focus:not(:active):not(:hover) {
    text-decoration: none;
  }
`;

const IconWrapper = styled.div`
  svg {
    width: 8px;
    height: 8px;
    stroke-width: 1.5;
  }
`;
